<template>
  <template v-if="compact">
    <div
      ref="pasteZone"
      class="relative aspect-square rounded-md border-2 border-dashed flex flex-col items-center justify-center text-center transition outline-none p-2 gap-2"
      :class="zoneClass"
      tabindex="0"
      :title="$t('smm.upload.tile_title')"
      @focus="focused = true"
      @blur="focused = false"
      @paste="onPaste"
      @dragover.prevent="dragOver = true"
      @dragleave.prevent="dragOver = false"
      @drop.prevent="onDrop"
    >
      <button
        type="button"
        class="flex flex-col items-center justify-center w-12 h-12 rounded-xl bg-white border-2 border-gray-300 text-gray-600 hover:border-blue-500 hover:bg-blue-50 hover:text-blue-700 active:scale-95 transition shadow-sm"
        :title="$t('smm.upload.paste_clipboard')"
        :aria-label="$t('smm.upload.paste_clipboard')"
        @click.stop="pasteFromClipboard"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.75" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" d="M15.666 3.888A2.25 2.25 0 0013.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 01-.75.75H9a.75.75 0 01-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 01-2.25 2.25H6.75A2.25 2.25 0 014.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 011.927-.184" />
        </svg>
      </button>
      <button
        type="button"
        class="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium text-gray-600 hover:bg-white/80"
        @click.stop="onAddTileClick"
      >
        <span class="text-base leading-none">+</span>
        <span>{{ $t('smm.upload.add') }}</span>
      </button>
      <span v-if="!small" class="text-[10px] text-gray-400 leading-tight px-1">{{ $t('smm.upload.ctrl_v_hint') }}</span>
      <input
        ref="fileInput"
        type="file"
        multiple
        accept="image/*,video/mp4,.mp4"
        class="hidden"
        @change="onFileInput"
      />
    </div>

    <div
      v-for="(item, index) in items"
      :key="item.id"
      class="relative group overflow-hidden border bg-white aspect-square cursor-grab active:cursor-grabbing"
      :class="small ? 'rounded-md' : 'rounded-lg'"
      draggable="true"
      @dragstart="onItemDragStart(index, $event)"
      @dragover.prevent
      @drop.prevent="onItemDrop(index)"
    >
      <img
        v-if="item.kind === 'image'"
        :src="item.previewUrl"
        :alt="item.name"
        class="w-full h-full object-cover"
        draggable="false"
        @dragstart.stop.prevent
      />
      <video
        v-else
        :src="item.previewUrl"
        class="w-full h-full object-cover pointer-events-none"
        muted
        playsinline
        preload="metadata"
      />
      <button
        v-if="item.kind === 'video'"
        type="button"
        class="absolute inset-0 flex items-center justify-center bg-black/25 hover:bg-black/40 transition"
        :title="$t('smm.upload.play')"
        @click.stop="$emit('play-video', { src: item.previewUrl, title: item.name })"
      >
        <span
          class="rounded-full bg-white/95 text-gray-900 flex items-center justify-center shadow text-gray-900"
          :class="small ? 'w-5 h-5 pl-px text-[10px]' : 'w-9 h-9 pl-0.5 text-sm'"
        >
          ▶
        </span>
      </button>
      <button
        type="button"
        class="absolute rounded-full bg-black/60 text-white opacity-0 group-hover:opacity-100 transition z-10"
        :class="small ? 'top-0.5 right-0.5 w-4 h-4 text-[10px] leading-none' : 'top-1 right-1 w-5 h-5 text-xs'"
        @click="remove(index)"
      >
        ×
      </button>
    </div>

    <p v-if="error" class="col-span-full text-sm text-red-600">{{ error }}</p>
  </template>

  <div v-else class="space-y-3">
    <div class="flex flex-wrap gap-2">
      <label class="cursor-pointer inline-flex items-center gap-2 bg-white border border-gray-300 hover:border-blue-500 rounded-lg px-3 py-2 text-sm text-gray-700">
        <span>📁</span>
        {{ $t('smm.upload.select_files') }}
        <input
          ref="fileInput"
          type="file"
          multiple
          accept="image/*,video/mp4,.mp4"
          class="hidden"
          @change="onFileInput"
        />
      </label>
      <button
        type="button"
        class="inline-flex items-center gap-2 bg-white border border-gray-300 hover:border-blue-500 rounded-lg px-3 py-2 text-sm text-gray-700"
        @click="pasteFromClipboard"
      >
        <span>📋</span>
        {{ $t('smm.upload.paste_clipboard') }}
      </button>
    </div>

    <div
      ref="pasteZone"
      class="border-2 border-dashed rounded-lg p-6 text-center transition outline-none cursor-pointer"
      :class="zoneClass"
      tabindex="0"
      @click="focusPasteZone"
      @focus="focused = true"
      @blur="focused = false"
      @paste="onPaste"
      @dragover.prevent="dragOver = true"
      @dragleave.prevent="dragOver = false"
      @drop.prevent="onDrop"
    >
      <p class="text-sm font-medium text-gray-700">{{ $t('smm.upload.paste_zone_title') }}</p>
      <p class="text-xs text-gray-500 mt-1 max-w-sm mx-auto">
        {{ $t('smm.upload.paste_zone_instruction') }}
      </p>
      <p v-if="focused" class="text-xs text-gray-700 mt-2">{{ $t('smm.upload.paste_ready') }}</p>
    </div>

    <p v-if="error" class="text-sm text-red-600">{{ error }}</p>

    <div v-if="items.length" class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
      <div
        v-for="(item, index) in items"
        :key="item.id"
        class="relative group rounded-lg overflow-hidden border bg-white aspect-square"
      >
        <img
          v-if="item.kind === 'image'"
          :src="item.previewUrl"
          :alt="item.name"
          class="w-full h-full object-cover"
        />
        <video
          v-else
          :src="item.previewUrl"
          class="w-full h-full object-cover pointer-events-none"
          muted
          playsinline
          preload="metadata"
        />
        <button
          v-if="item.kind === 'video'"
          type="button"
          class="absolute inset-0 flex items-center justify-center bg-black/25 hover:bg-black/40 transition"
          :title="$t('smm.upload.play')"
          @click.stop="$emit('play-video', { src: item.previewUrl, title: item.name })"
        >
          <span class="w-10 h-10 rounded-full bg-white/95 text-gray-900 flex items-center justify-center shadow pl-0.5">
            ▶
          </span>
        </button>
        <button
          type="button"
          class="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white text-xs opacity-0 group-hover:opacity-100 transition z-10"
          @click="remove(index)"
        >
          ×
        </button>
        <div class="absolute bottom-0 inset-x-0 bg-black/50 text-white text-[10px] px-1 py-0.5 truncate">
          {{ item.name }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
let nextId = 1

function isVideoFile(file) {
  return file.type.startsWith('video/') || /\.mp4$/i.test(file.name)
}

function isImageFile(file) {
  return file.type.startsWith('image/')
}

async function convertImageToPngIfNeeded(file, t) {
  const type = (file.type || '').toLowerCase()
  if (type === 'image/jpeg' || type === 'image/png') {
    return file
  }
  if (!isImageFile(file)) {
    return file
  }

  return new Promise((resolve, reject) => {
    const img = new Image()
    const url = URL.createObjectURL(file)
    img.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = img.naturalWidth
      canvas.height = img.naturalHeight
      const ctx = canvas.getContext('2d')
      ctx.drawImage(img, 0, 0)
      URL.revokeObjectURL(url)
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error(t('smm.upload.convert_png_error', { name: file.name })))
          return
        }
        const base = file.name.replace(/\.[^.]+$/, '') || 'image'
        resolve(new File([blob], `${base}.png`, { type: 'image/png' }))
      }, 'image/png')
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error(t('smm.upload.browser_open_error', { name: file.name })))
    }
    img.src = url
  })
}

export default {
  props: {
    modelValue: { type: Array, default: () => [] },
    compact: { type: Boolean, default: false },
    small: { type: Boolean, default: false },
  },
  emits: ['update:modelValue', 'play-video'],
  data: () => ({
    dragOver: false,
    focused: false,
    error: null,
    dragIndex: null,
  }),
  computed: {
    items: {
      get() {
        return this.modelValue
      },
      set(value) {
        this.$emit('update:modelValue', value)
      },
    },
    zoneClass() {
      if (this.dragOver) return 'border-blue-500 bg-blue-50'
      if (this.focused) return 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
      return 'border-gray-300 bg-gray-50 hover:border-gray-400'
    },
  },
  mounted() {
    window.addEventListener('paste', this.onWindowPaste)
  },
  beforeUnmount() {
    window.removeEventListener('paste', this.onWindowPaste)
    this.revokeAll()
  },
  methods: {
    onAddTileClick() {
      this.$refs.fileInput?.click()
    },
    onItemDragStart(index, event) {
      this.dragIndex = index
      event.dataTransfer.effectAllowed = 'move'
      try {
        event.dataTransfer.setData('text/plain', String(index))
      } catch (_) {
        // ignore
      }
    },
    onItemDrop(index) {
      if (this.dragIndex == null || this.dragIndex === index) {
        this.dragIndex = null
        return
      }
      const next = [...this.items]
      const [moved] = next.splice(this.dragIndex, 1)
      next.splice(index, 0, moved)
      this.items = next
      this.dragIndex = null
    },
    focusPasteZone() {
      this.$refs.pasteZone?.focus()
    },
    revokeAll() {
      this.items.forEach((item) => {
        if (item.previewUrl) URL.revokeObjectURL(item.previewUrl)
      })
    },
    extractImageFiles(dataTransferOrClipboard) {
      const items = dataTransferOrClipboard?.items
      if (!items) return []

      const files = []
      for (const item of items) {
        if (item.kind === 'file') {
          const file = item.getAsFile()
          if (file && (isImageFile(file) || isVideoFile(file))) {
            files.push(file)
          }
        }
      }
      return files
    },
    async addFiles(fileList) {
      this.error = null
      const files = Array.from(fileList || [])
      if (!files.length) return

      const added = []
      for (const raw of files) {
        try {
          if (isVideoFile(raw)) {
            if (raw.type && raw.type !== 'video/mp4' && !/\.mp4$/i.test(raw.name)) {
              throw new Error(this.$t('smm.upload.mp4_only', { name: raw.name }))
            }
            added.push({
              id: nextId++,
              file: raw,
              previewUrl: URL.createObjectURL(raw),
              name: raw.name,
              kind: 'video',
            })
            continue
          }

          if (!isImageFile(raw)) {
            throw new Error(this.$t('smm.upload.image_or_mp4', { name: raw.name }))
          }

          const file = await convertImageToPngIfNeeded(raw, this.$t.bind(this))
          added.push({
            id: nextId++,
            file,
            previewUrl: URL.createObjectURL(file),
            name: file.name,
            kind: 'image',
          })
        } catch (e) {
          this.error = e.message || this.$t('smm.upload.add_error')
        }
      }

      if (added.length) {
        this.items = [...this.items, ...added]
      }
    },
    onFileInput(e) {
      this.addFiles(e.target.files)
      e.target.value = ''
    },
    onPaste(e) {
      const files = this.extractImageFiles(e.clipboardData)
      if (files.length) {
        e.preventDefault()
        this.addFiles(files)
      }
    },
    onWindowPaste(e) {
      const tag = e.target?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA') {
        return
      }

      const files = this.extractImageFiles(e.clipboardData)
      if (files.length) {
        e.preventDefault()
        this.addFiles(files)
      }
    },
    async pasteFromClipboard() {
      this.error = null

      if (navigator.clipboard?.read) {
        try {
          const clipItems = await navigator.clipboard.read()
          const files = []
          for (const item of clipItems) {
            const type = item.types.find((t) => t.startsWith('image/'))
            if (type) {
              const blob = await item.getType(type)
              const ext = type === 'image/png' ? 'png' : type === 'image/jpeg' ? 'jpg' : 'png'
              files.push(new File([blob], `clipboard-${Date.now()}.${ext}`, { type }))
            }
          }
          if (files.length) {
            await this.addFiles(files)
            return
          }
        } catch {
          // fall through
        }
      }

      this.focusPasteZone()
      this.error = this.compact
        ? this.$t('smm.upload.paste_hint_compact')
        : this.$t('smm.upload.paste_hint_full')
    },
    onDrop(e) {
      this.dragOver = false
      this.addFiles(e.dataTransfer?.files)
    },
    remove(index) {
      const item = this.items[index]
      if (item?.previewUrl) URL.revokeObjectURL(item.previewUrl)
      this.items = this.items.filter((_, i) => i !== index)
    },
    clear() {
      this.revokeAll()
      this.items = []
      this.error = null
      this.focused = false
    },
  },
}
</script>
